import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Plus, Trash2, CheckCircle2, AlertTriangle, DollarSign, PenLine } from "lucide-react";

function money(v) { return `$${Number(v || 0).toFixed(2)}`; }

const TYPE_ORDER = ["bill", "coin_roll", "coin", "other"];
const TYPE_LABEL = { bill: "Bills", coin_roll: "Coin Rolls", coin: "Coins", other: "Other" };

export default function FloatRecord() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const { correctionOf } = useParams();
  const [registers, setRegisters] = useState([]);
  const [denominations, setDenominations] = useState([]);
  const [registerId, setRegisterId] = useState("");
  const [takenLines, setTakenLines] = useState([]);
  const [replaceLines, setReplaceLines] = useState([]);
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [correctionReason, setCorrectionReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pickerSide, setPickerSide] = useState(null);
  const [original, setOriginal] = useState(null);

  useEffect(() => {
    api.get("/registers").then(({ data }) => {
      const active = data.filter((r) => r.active !== false);
      setRegisters(active);
      if (active.length === 1) setRegisterId(active[0].id);
    }).catch((e) => toast.error(formatApiError(e)));
    api.get("/denominations").then(({ data }) => setDenominations(data.filter((d) => d.active !== false))).catch(() => {});
  }, []);

  useEffect(() => {
    if (correctionOf) {
      api.get(`/float_changes/${correctionOf}`).then(({ data }) => {
        setOriginal(data);
        setRegisterId(data.register_id);
      }).catch((e) => toast.error(formatApiError(e)));
    }
  }, [correctionOf]);

  const denomMap = useMemo(() => Object.fromEntries(denominations.map((d) => [d.id, d])), [denominations]);
  const denomByType = useMemo(() => {
    const m = {};
    for (const d of denominations) (m[d.type] = m[d.type] || []).push(d);
    return m;
  }, [denominations]);

  const totalOf = (lines) => lines.reduce((sum, l) => {
    const d = denomMap[l.denomination_id];
    return sum + (d ? Number(d.unit_value) * (l.quantity || 0) : 0);
  }, 0);
  const takenTotal = totalOf(takenLines);
  const replaceTotal = totalOf(replaceLines);
  const diff = Number((takenTotal - replaceTotal).toFixed(2));
  const isBalanced = Math.abs(diff) < 0.005;

  const addLine = (side, denominationId) => {
    const setter = side === "taken" ? setTakenLines : setReplaceLines;
    setter((prev) => {
      const existing = prev.find((l) => l.denomination_id === denominationId);
      if (existing) return prev.map((l) => l.denomination_id === denominationId ? { ...l, quantity: l.quantity + 1 } : l);
      return [...prev, { denomination_id: denominationId, quantity: 1 }];
    });
    setPickerSide(null);
  };
  const removeLine = (side, i) => {
    const setter = side === "taken" ? setTakenLines : setReplaceLines;
    setter((prev) => prev.filter((_, idx) => idx !== i));
  };
  const setQty = (side, i, q) => {
    const qty = Math.max(0, Number(q) || 0);
    const setter = side === "taken" ? setTakenLines : setReplaceLines;
    setter((prev) => prev.map((l, idx) => idx === i ? { ...l, quantity: qty } : l));
  };

  const canProceed = registerId && takenLines.length > 0 && replaceLines.length > 0 && takenLines.every((l) => l.quantity > 0) && replaceLines.every((l) => l.quantity > 0);

  const submit = async () => {
    if (!confirmed) return toast.error("Please confirm before recording");
    if (!isBalanced && !reason.trim()) return toast.error("Reason for difference is required");
    if (correctionOf && !correctionReason.trim()) return toast.error("Reason for correction is required");
    setBusy(true);
    try {
      const body = {
        register_id: registerId,
        taken_lines: takenLines,
        replacement_lines: replaceLines,
        notes: notes.trim(),
        mismatch_reason: isBalanced ? "" : reason.trim(),
        manager_confirmed: true,
      };
      let saved;
      if (correctionOf) {
        const { data } = await api.post(`/float_changes/${correctionOf}/correction`, { ...body, original_id: correctionOf, reason: correctionReason.trim() });
        saved = data;
      } else {
        const { data } = await api.post("/float_changes", body);
        saved = data;
      }
      toast.success(`Recorded ${saved.reference_number}`);
      nav(`/float/${saved.id}`, { state: { justRecorded: true } });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const currentRegister = registers.find((r) => r.id === registerId);

  return (
    <div className="space-y-4 max-w-2xl mx-auto pb-24" data-testid="float-record-page">
      <button onClick={() => nav(-1)} className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Back</button>
      <div>
        <div className="label-caps">{correctionOf ? "Correction" : "New Entry"}</div>
        <h1 className="font-heading text-2xl sm:text-3xl">{correctionOf ? "Correct Float Change" : "Record Float Change"}</h1>
        {original && (
          <div className="mt-2 text-xs text-[color:var(--text-secondary)]">
            Correcting <span className="text-[color:var(--gold)]">{original.reference_number}</span> · {original.register_name} · originally {money(original.taken_total)} → {money(original.replacement_total)}
          </div>
        )}
      </div>

      {correctionOf && (
        <div className="hos-card p-4">
          <label className="label-caps">Reason for Correction (required)</label>
          <textarea className="hos-input min-h-[60px]" placeholder="Explain what was wrong in the original entry…" value={correctionReason} onChange={(e) => setCorrectionReason(e.target.value)} data-testid="correction-reason"/>
        </div>
      )}

      <div className="hos-card p-4">
        <label className="label-caps">Register</label>
        <select className="hos-input" value={registerId} onChange={(e) => setRegisterId(e.target.value)} disabled={!!correctionOf} data-testid="register-select">
          <option value="">— select register —</option>
          {registers.map((r) => <option key={r.id} value={r.id}>{r.name}{r.location ? ` — ${r.location}` : ""}</option>)}
        </select>
        {currentRegister && <div className="text-xs text-[color:var(--text-secondary)] mt-1">Date/Time captured automatically on submit.</div>}
      </div>

      <LineSection
        title="Taken from Register"
        testId="taken"
        lines={takenLines}
        denomMap={denomMap}
        total={takenTotal}
        onAdd={() => setPickerSide("taken")}
        onRemove={(i) => removeLine("taken", i)}
        onQty={(i, q) => setQty("taken", i, q)}
      />

      <LineSection
        title="Replaced With"
        testId="replace"
        lines={replaceLines}
        denomMap={denomMap}
        total={replaceTotal}
        onAdd={() => setPickerSide("replace")}
        onRemove={(i) => removeLine("replace", i)}
        onQty={(i, q) => setQty("replace", i, q)}
      />

      <MatchBanner takenTotal={takenTotal} replaceTotal={replaceTotal} diff={diff} isBalanced={isBalanced}/>

      {!isBalanced && (
        <div className="hos-card p-4">
          <label className="label-caps text-[color:var(--warning)]">Reason for Difference (required)</label>
          <textarea className="hos-input min-h-[70px]" placeholder="e.g. Change fund short — needs restock from safe" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="mismatch-reason"/>
        </div>
      )}

      <div className="hos-card p-4">
        <label className="label-caps">Notes (optional)</label>
        <input className="hos-input" placeholder="e.g. Needed additional dimes for customer change" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="float-notes"/>
      </div>

      <button
        className="hos-btn-primary w-full text-lg py-3 flex items-center justify-center gap-2"
        disabled={!canProceed}
        onClick={() => setShowConfirm(true)}
        data-testid="continue-btn"
      >
        <PenLine size={18}/> Review & Sign
      </button>

      {pickerSide && (
        <DenomPicker
          side={pickerSide}
          denomByType={denomByType}
          onPick={(d) => addLine(pickerSide, d.id)}
          onClose={() => setPickerSide(null)}
        />
      )}

      {showConfirm && (
        <ConfirmSheet
          registerName={currentRegister?.name || ""}
          storeName={currentRegister ? (registers.find((r) => r.id === registerId)?.store_name || "") : ""}
          managerName={user?.name}
          takenLines={takenLines.map((l) => ({ ...l, ...denomMap[l.denomination_id] }))}
          replaceLines={replaceLines.map((l) => ({ ...l, ...denomMap[l.denomination_id] }))}
          takenTotal={takenTotal} replaceTotal={replaceTotal} diff={diff} isBalanced={isBalanced}
          confirmed={confirmed} setConfirmed={setConfirmed}
          onSubmit={submit} onCancel={() => setShowConfirm(false)} busy={busy}
        />
      )}
    </div>
  );
}

function LineSection({ title, testId, lines, denomMap, total, onAdd, onRemove, onQty }) {
  return (
    <div className="hos-card p-4" data-testid={`section-${testId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading text-lg uppercase tracking-wide">{title}</div>
        <div className="text-right">
          <div className="text-[10px] label-caps">Total</div>
          <div className="font-heading text-xl text-[color:var(--gold)]" data-testid={`${testId}-total`}>{money(total)}</div>
        </div>
      </div>
      <div className="space-y-2">
        {lines.map((l, i) => {
          const d = denomMap[l.denomination_id];
          if (!d) return null;
          const amt = Number(d.unit_value) * (l.quantity || 0);
          return (
            <div key={i} className="flex items-center gap-2 p-2 rounded border border-[color:var(--border)]" data-testid={`${testId}-line-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{d.name}</div>
                <div className="text-[10px] text-[color:var(--text-secondary)]">× {money(d.unit_value)}</div>
              </div>
              <input type="number" min="1" className="hos-input w-20 py-1 text-center" value={l.quantity} onChange={(e) => onQty(i, e.target.value)} data-testid={`${testId}-qty-${i}`}/>
              <div className="w-20 text-right font-heading text-[color:var(--gold)]">{money(amt)}</div>
              <button onClick={() => onRemove(i)} className="text-[color:var(--error)] p-1" data-testid={`${testId}-rm-${i}`}><Trash2 size={14}/></button>
            </div>
          );
        })}
      </div>
      <button onClick={onAdd} className="mt-3 hos-btn-outline w-full flex items-center justify-center gap-1 py-2" data-testid={`${testId}-add`}>
        <Plus size={14}/> Add Denomination
      </button>
    </div>
  );
}

function MatchBanner({ takenTotal, replaceTotal, diff, isBalanced }) {
  if (!takenTotal && !replaceTotal) return null;
  const cls = isBalanced
    ? "border-emerald-400/50 bg-emerald-500/10 text-emerald-300"
    : "border-[color:var(--warning)]/50 bg-[color:var(--warning)]/10 text-[color:var(--warning)]";
  return (
    <div className={`hos-card p-4 border ${cls}`} data-testid="match-banner">
      <div className="flex items-center gap-2 font-bold text-lg">
        {isBalanced ? <CheckCircle2 size={20}/> : <AlertTriangle size={20}/>}
        {isBalanced ? "AMOUNTS MATCH" : "AMOUNTS DO NOT MATCH"}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
        <div><div className="label-caps">Taken</div><div className="font-heading">{money(takenTotal)}</div></div>
        <div><div className="label-caps">Replaced</div><div className="font-heading">{money(replaceTotal)}</div></div>
        <div><div className="label-caps">Diff</div><div className="font-heading" data-testid="diff-value">{diff >= 0 ? "" : "−"}${Math.abs(diff).toFixed(2)}</div></div>
      </div>
    </div>
  );
}

function DenomPicker({ side, denomByType, onPick, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="hos-card p-4 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="denom-picker">
        <div className="font-heading text-xl mb-3">Add to {side === "taken" ? "TAKEN" : "REPLACED"}</div>
        {TYPE_ORDER.map((t) => {
          const items = denomByType[t];
          if (!items || items.length === 0) return null;
          return (
            <div key={t} className="mb-3">
              <div className="label-caps mb-2">{TYPE_LABEL[t]}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {items.map((d) => (
                  <button key={d.id} onClick={() => onPick(d)}
                          className="p-3 rounded-md border border-[color:var(--border)] hover:border-[color:var(--gold)] text-left"
                          data-testid={`pick-${d.id}`}>
                    <div className="font-semibold text-sm">{d.name}</div>
                    <div className="text-[11px] text-[color:var(--gold)]">${Number(d.unit_value).toFixed(2)}</div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        <button onClick={onClose} className="hos-btn-outline w-full mt-2">Cancel</button>
      </div>
    </div>
  );
}

function ConfirmSheet({ registerName, managerName, takenLines, replaceLines, takenTotal, replaceTotal, diff, isBalanced, confirmed, setConfirmed, onSubmit, onCancel, busy }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="hos-card p-6 max-w-md w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="confirm-sheet">
        <div className="font-heading text-2xl mb-4">Confirm Float Change</div>
        <div className="space-y-3 text-sm">
          <div><div className="label-caps">Register</div>{registerName}</div>
          <div>
            <div className="label-caps mb-1">Taken</div>
            {takenLines.map((l, i) => <div key={i} className="flex justify-between"><span>{l.quantity} × {l.name}</span><span className="text-[color:var(--gold)]">${(Number(l.unit_value) * l.quantity).toFixed(2)}</span></div>)}
            <div className="border-t border-[color:var(--border)] mt-1 pt-1 flex justify-between font-heading"><span>Total</span><span>${takenTotal.toFixed(2)}</span></div>
          </div>
          <div>
            <div className="label-caps mb-1">Replaced With</div>
            {replaceLines.map((l, i) => <div key={i} className="flex justify-between"><span>{l.quantity} × {l.name}</span><span className="text-[color:var(--gold)]">${(Number(l.unit_value) * l.quantity).toFixed(2)}</span></div>)}
            <div className="border-t border-[color:var(--border)] mt-1 pt-1 flex justify-between font-heading"><span>Total</span><span>${replaceTotal.toFixed(2)}</span></div>
          </div>
          <div className={`p-3 rounded ${isBalanced ? "bg-emerald-500/10 text-emerald-300" : "bg-[color:var(--warning)]/10 text-[color:var(--warning)]"}`}>
            <div className="flex justify-between font-heading text-lg">
              <span>Difference</span>
              <span>{isBalanced ? "$0.00" : `${diff >= 0 ? "" : "−"}$${Math.abs(diff).toFixed(2)}`}</span>
            </div>
            <div className="text-xs">{isBalanced ? "✓ Amounts Match" : "⚠ Reason required"}</div>
          </div>
          <div><div className="label-caps">Manager</div>{managerName}</div>
        </div>
        <label className="flex items-start gap-2 mt-4 text-sm p-3 rounded border border-[color:var(--border)]">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-1" data-testid="confirm-check"/>
          <span>I confirm that I completed this float denomination exchange and that the information above is accurate.</span>
        </label>
        <div className="flex flex-col gap-2 mt-4">
          <button className="hos-btn-primary flex items-center justify-center gap-2" disabled={!confirmed || busy} onClick={onSubmit} data-testid="sign-record-btn">
            <DollarSign size={18}/> {busy ? "Recording…" : "Sign & Record"}
          </button>
          <button className="hos-btn-outline" onClick={onCancel} disabled={busy}>Go Back</button>
        </div>
      </div>
    </div>
  );
}
